import FormattedMessage from 'I18n/components/FormattedMessage';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { deleteTask as deleteTaskRequest, getTaskById as getTaskByIdRequest, updateTask as updateTaskRequest } from '../../api/tasks';
import { postCreateFollowUpTask } from 'task-forms-lib/api/TaskApi';
import { TASK_FORM_READ_PROPERTIES } from '../../constants/properties';
import { addError, GROUP_NAMES, reportAndDisplayError, reportNewTasksError } from '../../tracking/errors';
import { trackBulkDeletedTasks, trackBulkEditedProperty } from '../../tracking/tracker';
import { jsx as _jsx } from "react/jsx-runtime";
function maybeShowSuccessAlert({
  messageKey,
  count,
  type = 'success'
}) {
  if (messageKey) {
    FloatingAlertStore.addAlert({
      type,
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: messageKey,
        options: {
          count
        }
      })
    });
  }
}
function logErrorAndMaybeShowAlert({
  count,
  error,
  functionName,
  messageI18nKey
}) {
  const errorInfo = {
    groupName: GROUP_NAMES.TASK_CREATE_UPDATE_DELETE,
    functionName,
    extra: {
      messageI18nKey,
      count
    }
  };
  if (messageI18nKey) {
    reportAndDisplayError(error, {
      messageI18nKey,
      options: {
        count
      }
    }, errorInfo);
  } else {
    reportNewTasksError(error, errorInfo);
  }
}

// TODO: should be using this function only once all bulk actions are
// migrated to customer-data-bulk-actions and can do some cleanup
export const logBulkActionUpdate = (didRequestSucceed, logInformation) => {
  if (didRequestSucceed) {
    return trackBulkEditedProperty(logInformation);
  } else {
    const {
      error,
      count,
      functionName
    } = logInformation;

    // customer-data-bulk-actions handles the alerts so only need to report the error
    return logErrorAndMaybeShowAlert({
      count,
      error,
      functionName
    });
  }
};
export const logBulkActionDelete = (didRequestSucceed, logInformation) => {
  if (didRequestSucceed) {
    return trackBulkDeletedTasks(logInformation);
  } else {
    const {
      error,
      count,
      functionName
    } = logInformation;

    // customer-data-bulk-actions handles the alerts so only need to report the error
    return logErrorAndMaybeShowAlert({
      count,
      error,
      functionName
    });
  }
};
function showAlertAndMaybeLogError(error, displayErrorArgs, reportErrorArgs) {
  if (error.status === 404) {
    addError(displayErrorArgs);
  } else {
    reportAndDisplayError(error, displayErrorArgs, reportErrorArgs);
  }
}
export const mergeTaskPropertyUpdates = createAction('mergeTaskPropertyUpdates');
export const addNewTask = createAction('addNewTask');
export const createFollowUpTask = createAsyncThunk('createFollowUpTask', ({
  failureMessageI18nKey = 'taskForm.error.createTaskFailed',
  relativeReminders,
  reminders = [],
  taskIdToFollowUpOn,
  timestamp
}) => {
  if (!timestamp) {
    return null;
  }
  return postCreateFollowUpTask({
    taskIdToFollowUpOn,
    timestamp,
    reminders,
    relativeReminders
  }).then(createdTask => {
    return {
      task: createdTask
    };
  }, __error => {
    addError({
      messageI18nKey: failureMessageI18nKey
    });
  });
});
export const updateTask = createAsyncThunk('updateTask', ({
  objectId,
  propertyUpdates,
  associations,
  onSuccess = () => {},
  onFailure = () => {},
  successMessageI18nKey = 'tasksAlerts.bulkActions.generic.success',
  failureMessageI18nKey = 'tasksAlerts.bulkActions.generic.failure',
  updateTemplate = false
}) => {
  return updateTaskRequest({
    objectId,
    properties: propertyUpdates,
    associations
  }, {
    updateTemplate
  }).then(taskRecord => {
    maybeShowSuccessAlert({
      messageKey: successMessageI18nKey,
      count: 1
    });
    onSuccess(taskRecord);
    return {
      task: taskRecord
    };
  }, error => {
    showAlertAndMaybeLogError(error, {
      messageI18nKey: failureMessageI18nKey,
      options: {
        count: 1
      }
    }, {
      functionName: 'updateTask',
      groupName: GROUP_NAMES.TASK_CREATE_UPDATE_DELETE,
      extra: {
        messageI18nKey: failureMessageI18nKey,
        count: 1
      }
    });
    onFailure(error);
  });
});
export const deleteTask = createAsyncThunk('deleteTask', ({
  taskId,
  activeViewId,
  stopRepeating
}) => {
  return deleteTaskRequest(taskId, {
    stopRepeating
  }).then(() => {
    return {
      activeViewId,
      taskId
    };
  }, error => {
    const messageI18nKey = 'taskForm.error.deleteTaskFailed';
    showAlertAndMaybeLogError(error, {
      messageI18nKey,
      options: {
        count: 1
      }
    }, {
      functionName: 'deleteTask',
      groupName: GROUP_NAMES.TASK_CREATE_UPDATE_DELETE,
      extra: {
        messageI18nKey,
        count: 1
      }
    });
  });
});
export const deleteTasks = createAction('deleteTasks');
export const deleteAllTasks = createAction('deleteAllTasks');
export const getTaskById = createAsyncThunk('getTaskById', ({
  taskId,
  onDone = () => {}
}) => {
  return getTaskByIdRequest(taskId, TASK_FORM_READ_PROPERTIES).then(taskRecord => {
    return {
      task: taskRecord
    };
  }, error => {
    const displayErrorArgs = {
      titleI18nKey: 'tasksAlerts.titles.notFoundTask',
      messageI18nKey: 'tasksAlerts.getTaskFailed',
      errorType: 'danger'
    };
    const reportErrorArgs = {
      groupName: GROUP_NAMES.TASK_READ,
      functionName: 'openEditTaskForm'
    };
    showAlertAndMaybeLogError(error, displayErrorArgs, reportErrorArgs);
  }).finally(onDone);
});